import type { ItemFieldRow } from './item-field-reader';
import { isOwnedRuleWindow } from './owned-rule-window';
import { createFormulaReadContext } from './formula-read-context';
import { createAbilityConditionEvaluator } from './ability-conditions';
import type { AbilityConditionDependencies } from './ability-conditions';
import { createInventoryReadContext } from './inventory-read-context';
import { readItemScalarLayerSlot, readOverrideEntryAbilityId } from './layer-cell-readers';
import { layerEntryNeedsResolve, resolveLayerEntry } from './layer-entry-resolver';
import { appendPhpCompileArray } from './status-row-construction';
import { phpJsonTruthy } from './php-json-values';

/** FT_COMPUTE_SENSES_NAMES (compute/senses.php; TS FT_SENSE_NAMES in creature-senses-compile.ts) — the layers the defense merge
 *  never root-merges from a gear passive (2026-10-08). Local copy: importing the senses compile here would pull its whole graph. */
const GEAR_PASSIVE_UNMERGED_SENSES: ReadonlySet<string> = new Set(['nightsight', 'fiendsight', 'blindsight', 'ghostsight', 'spellsight', 'truesight', 'tremorsense', 'telepathy', 'windsense']);

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const has = (v: unknown, k: string): boolean => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, k);
const own = (v: unknown, k: string): unknown => has(v, k) ? (v as Row)[k] : undefined;
const path = (v: unknown, ...keys: string[]): unknown => keys.reduce(own, v);
const set = (row: Row, key: string, value: unknown): void => { Object.defineProperty(row, key, {value, enumerable: true, writable: true, configurable: true}); };
function copy<T>(v: T): T {
    if (!arrayLike(v)) return v;
    const entries = Object.entries(v);
    return (entries.every(([key], i) => key === String(i)) ? entries.map(([, child]) => copy(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, copy(child)]))) as T;
}
export class AbilityFinalizerArrayWriteError extends Error {}
function writable(value: unknown): Row {
    if (value == null || value === false) return {};
    if (!arrayLike(value)) {
        if (typeof value === 'string') throw new TypeError('Cannot access string offset by named key');
        throw new AbilityFinalizerArrayWriteError('Cannot use scalar as array');
    }
    return Object.fromEntries(Object.entries(value));
}
function write(row: Row, keys: string[], value: unknown): void {
    const [key, ...rest] = keys; if (key === undefined) throw new Error('Missing finalizer write key');
    if (rest.length === 0) { set(row, key, value); return; }
    const child = writable(own(row, key)); write(child, rest, value); set(row, key, child);
}
export interface AbilityFinalizerGateDependencies extends AbilityConditionDependencies {
    defenseIntegerLayers: readonly string[];
    queryFullItem: (id: string) => ItemFieldRow | null;
    /** Native origin guard, supplied by hosts that construct complete gear/templates. */
    gearPassiveCountedOnEquippedRow?: (host: ItemFieldRow, entry: ItemFieldRow, layer: string) => boolean;
}
/** Adjacent native finalizer stages: computed override decisions, then
 * inventory defense-fragment merge and raw ability condition pruning.
 */
export function createAbilityFinalizerGates(deps: AbilityFinalizerGateDependencies) {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const common = {...deps, inventoryContext}, readers = createFormulaReadContext(common), conditions = createAbilityConditionEvaluator(common);
    const entries = (row: ItemFieldRow | null, layer: string): ItemFieldRow => readers.layerEntries(row, layer);
    const hasLayer = (row: ItemFieldRow, layer: string): boolean => readers.layerValue(row, layer) !== null || phpJsonTruthy(entries(row, layer));
    const resolve = (entry: unknown, host: ItemFieldRow): unknown => layerEntryNeedsResolve(entry)
        ? resolveLayerEntry(entry, host, id => { emit(['full-item', id]); return deps.queryFullItem(id); }) : entry;
    function creatureEntries(host: ItemFieldRow | null, type: string): ItemFieldRow {
        if (host === null) return [];
        let result = entries(host, type);
        const derived = path(host, 'computed', 'creature', 'skill_ability', type);
        if (!arrayLike(derived) || Object.keys(derived).length === 0) return result;
        const seen = new Set<string>();
        for (const entry of Object.values(result)) { const id = readOverrideEntryAbilityId(entry); if (id !== '') seen.add(id); }
        for (const entry of Object.values(derived)) {
            const id = readOverrideEntryAbilityId(entry); if (id !== '' && seen.has(id)) continue;
            if (id !== '') seen.add(id); result = appendPhpCompileArray(result, entry);
        }
        return result;
    }
    function override(input: ItemFieldRow | null): ItemFieldRow | null {
        if (input !== null && !arrayLike(input)) throw new TypeError('Ability override requires a PHP array or null');
        const owned = isOwnedRuleWindow(input);
        const host = input === null ? null : owned ? input as Row : Object.fromEntries(Object.entries(copy(input))), ids = new Set<string>();
        const collect = (row: ItemFieldRow): void => {
            for (const entry of Object.values(entries(row, 'override_ability'))) { const id = readItemScalarLayerSlot(entry); if (id !== '') ids.add(id); }
        };
        for (const type of deps.abilityTypes) for (const ability of Object.values(creatureEntries(host, type))) {
            if (!arrayLike(ability)) continue;
            const nested = own(ability, 'item_arr');
            for (const source of [...(arrayLike(nested) ? [nested] : []), ability]) if (hasLayer(source, 'override_ability')) collect(source);
        }
        for (const entry of Object.values(entries(host, 'feat'))) {
            let feat: unknown = arrayLike(own(entry, 'item_arr')) ? own(entry, 'item_arr') : entry;
            // Nonempty feat entries imply a real host, but retain the typed boundary.
            if (host === null) throw new TypeError('Cannot resolve a feat on null');
            feat = resolve(feat, host); if (!arrayLike(feat) || !conditions.check(feat, host, false, false)) continue;
            for (const type of new Set([...deps.abilityTypes, 'passive_ability'])) for (const grant of Object.values(entries(feat, type))) {
                let ability = arrayLike(grant) ? own(grant, 'item_arr') ?? own(grant, 'item_id') ?? grant : grant;
                ability = resolve(ability, host);
                if (!arrayLike(ability) || !hasLayer(ability, 'override_ability') || !conditions.check(ability, host, false, false)) continue;
                collect(ability);
            }
        }
        if (ids.size === 0 || host === null) return owned ? host : copy(host);
        const previous = path(host, 'computed', 'creature', 'suppressed_ability_ids') ?? [];
        if (!arrayLike(previous)) throw new TypeError('PHP array union requires arrays');
        const union = Object.fromEntries(Object.entries(previous));
        for (const id of ids) if (!has(union, id)) set(union, id, true);
        write(host, ['computed', 'creature', 'suppressed_ability_ids'], union);
        for (const type of deps.abilityTypes) {
            const slot = path(host, 'computed', 'creature', 'skill_ability', type); if (!arrayLike(slot)) continue;
            let filtered: ItemFieldRow | null;
            if (has(slot, '0')) {
                const kept = Object.values(slot).filter(entry => { const id = readOverrideEntryAbilityId(entry); return id === '' || !ids.has(id); });
                filtered = kept.length ? kept : null;
            } else { const id = readOverrideEntryAbilityId(slot); filtered = id !== '' && ids.has(id) ? null : slot; }
            if (filtered === null) {
                const skills = writable(path(host, 'computed', 'creature', 'skill_ability')); delete skills[type];
                write(host, ['computed', 'creature', 'skill_ability'], skills);
            } else write(host, ['computed', 'creature', 'skill_ability', type], filtered);
        }
        return owned ? host : copy(host);
    }
    function prune(input: ItemFieldRow): ItemFieldRow {
        if (!arrayLike(input)) throw new TypeError('Defense merge/prune requires a PHP array');
        const owned = isOwnedRuleWindow(input);
        const host = owned ? input as Row : Object.fromEntries(Object.entries(copy(input)));
        for (const layer of ['feat', 'passive_ability', 'status_effect_permanent']) {
            const bucket = own(host, layer); if (!arrayLike(bucket)) continue;
            for (const entry of Object.values(bucket)) {
                if (!arrayLike(entry) || !hasLayer(entry, 'inventory_source')) continue;
                const ability = own(entry, 'item_arr');
                if (!arrayLike(ability) || !conditions.check(ability, host, false, false)) continue;
                for (const key of deps.defenseIntegerLayers) {
                    // 2026-10-08: a gear PASSIVE's sense is never root-merged — compute_senses' passive + equipped walks already score
                    // it (Devilfolk Commander nightsight 360 → 240). Twin of the PHP skip in merge_inventory_defense_ints_and_prune_abilities.
                    if (layer === 'passive_ability' && GEAR_PASSIVE_UNMERGED_SENSES.has(key)) continue;
                    if (layer === 'passive_ability' && deps.gearPassiveCountedOnEquippedRow?.(host, entry, key)) continue;
                    const fragments = Object.values(entries(ability, key));
                    for (const fragment of fragments) {
                        // 2026-10-08 (Devilfolk Commander fatal): a SCALAR host slot ("+120" — the creature's own nightsight) is promoted to a
                        // one-element list before the append, like the PHP twin (merge_inventory_defense_ints_and_prune_abilities) — no longer
                        // a write error. null / false / arrays append exactly as before.
                        const raw = own(host, key);
                        const old = raw != null && raw !== false && !arrayLike(raw) ? [raw] : raw;
                        set(host, key, appendPhpCompileArray(arrayLike(old) ? old : [], fragment));
                    }
                    // 2026-10-07: who put damage_mitigation into the host slot (computed._cache.dm_root_merged — the mitigation panel's names)
                    if (key === 'damage_mitigation' && fragments.length) {
                        const prior = path(host, 'computed', '_cache', 'dm_root_merged');
                        const list = arrayLike(prior) ? Object.values(prior) : [];
                        const text = (v: unknown): string => v == null ? '' : typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v === true ? 1 : v === false ? '' : v) : '';
                        list.push({ id: text(own(entry, 'ability_id') ?? readers.itemField(ability, 'item_id') ?? '').toUpperCase(),
                            name: text(readers.itemField(ability, 'item_name') ?? own(entry, 'item_name') ?? ''), bucket: layer, fragments });
                        write(host, ['computed', '_cache', 'dm_root_merged'], list);
                    }
                }
            }
        }
        for (const type of deps.abilityTypes) {
            const bucket = own(host, type); if (!arrayLike(bucket)) continue;
            for (const [key, entry] of Object.entries(bucket)) {
                if (arrayLike(entry) && hasLayer(entry, 'item_arr') && !conditions.check((own(entry, 'item_arr') ?? null) as ItemFieldRow | null, host)) {
                    const next = writable(own(host, type));
                    if (type === 'passive_ability') { const row = writable(entry); set(row, 'passive_conditions_active', false); set(next, key, row); }
                    else delete next[key];
                    set(host, type, next);
                }
            }
            set(host, type, Object.values(own(host, type) as ItemFieldRow));
        }
        return owned ? host : copy(host);
    }
    return {override, prune, all: (input: ItemFieldRow): ItemFieldRow => prune(override(input) as ItemFieldRow)};
}
